import React, { useState, useEffect } from 'react';
import API from '../services/api';

/* ============================================================
   Utilities
============================================================ */
function formatDateTime(raw) {
  if (!raw) return '';
  const d = new Date(raw);
  return d.toLocaleString();
}
function money(n) {
  return `$${(parseFloat(n) || 0).toFixed(2)}`;
}

/* ============================================================
   Submission Log

   Read-only history of "Mark Selected as Paid" batches. Each event
   row expands to the per-therapist breakdown, including that batch's
   take-home and the therapist's running pay-period take-home snapshot.
============================================================ */
function AdminSubmissionLogPage() {
  const [events, setEvents] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const LIMIT = 20;

  const fetchLog = async (reset = true) => {
    try {
      setLoading(true);
      const off = reset ? 0 : offset;
      const parts = [`limit=${LIMIT}`, `offset=${off}`];
      if (start) parts.push(`start=${start}`);
      if (end) parts.push(`end=${end}`);
      const res = await API.get(`/admin/submission-log?${parts.join('&')}`);
      const data = res.data || {};
      const rows = data.events || [];
      if (reset) {
        setEvents(rows);
        setOffset(rows.length);
      } else {
        setEvents((prev) => [...prev, ...rows]);
        setOffset((prev) => prev + rows.length);
      }
      setHasMore(!!data.hasMore);
    } catch (err) {
      console.error('Error fetching submission log:', err);
      alert('Error fetching submission log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLog(true);
    // eslint-disable-next-line
  }, []);

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div style={containerStyle}>
      <header style={pageHeaderStyle}>
        <div>
          <h2 style={{ margin: 0 }}>Submission Log</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            A record of every “Mark Selected as Paid” batch, with the per-therapist split.
          </p>
        </div>
      </header>

      <section style={cardStyle}>
        {/* Filters */}
        <div style={filtersRowStyle}>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Start Date</label>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={inputStyle} />
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>End Date</label>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ ...filterGroupStyle, alignSelf: 'flex-end' }}>
            <button onClick={() => fetchLog(true)} style={btnPrimaryStyle}>Search</button>
          </div>
          {(start || end) && (
            <div style={{ ...filterGroupStyle, alignSelf: 'flex-end' }}>
              <button
                onClick={() => { setStart(''); setEnd(''); setTimeout(() => fetchLog(true), 0); }}
                style={btnStyle}
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {loading && events.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
        ) : events.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>No submission batches recorded yet.</p>
        ) : (
          <>
            <div style={tableWrapStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}></th>
                    <th style={thStyle}>Date / Time</th>
                    <th style={thStyle}>Marked By</th>
                    <th style={thStyle}>Sessions</th>
                    <th style={thStyle}>Total Collected</th>
                    <th style={thStyle}>Therapist Pay</th>
                    <th style={thStyle}>Practice</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => {
                    const isOpen = !!expanded[ev.id];
                    return (
                      <React.Fragment key={ev.id}>
                        <tr
                          style={{ cursor: 'pointer', background: isOpen ? '#f0f9ff' : '#fff' }}
                          onClick={() => toggleExpand(ev.id)}
                        >
                          <td style={tdStyle}>
                            <span style={{ fontWeight: 700, color: 'var(--text-muted)' }}>
                              {isOpen ? '▾' : '▸'}
                            </span>
                          </td>
                          <td style={tdStyle}>{formatDateTime(ev.created_at)}</td>
                          <td style={tdStyle}>{ev.admin_name || '—'}</td>
                          <td style={tdStyle}>{ev.session_count}</td>
                          <td style={tdStyle}>{money(ev.total_collected)}</td>
                          <td style={tdStyle}>{money(ev.total_therapist_pay)}</td>
                          <td style={tdStyle}>{money(ev.total_practice)}</td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td></td>
                            <td colSpan={6} style={{ padding: '0 12px 12px' }}>
                              <table style={detailTableStyle}>
                                <thead>
                                  <tr>
                                    <th style={detailThStyle}>Therapist</th>
                                    <th style={detailThStyle}>Sessions</th>
                                    <th style={detailThStyle}>Collected</th>
                                    <th style={detailThStyle}>Split %</th>
                                    <th style={detailThStyle}>Therapist Pay</th>
                                    <th style={detailThStyle}>Practice</th>
                                    <th style={detailThStyle}>Pay-Period Take-Home</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(ev.details || []).map((d, i) => (
                                    <tr key={i}>
                                      <td style={detailTdStyle}>{d.therapist_name || '—'}</td>
                                      <td style={detailTdStyle}>{d.session_count}</td>
                                      <td style={detailTdStyle}>{money(d.total_collected)}</td>
                                      <td style={detailTdStyle}>{(parseFloat(d.pay_split) || 0).toFixed(2)}%</td>
                                      <td style={detailTdStyle}>{money(d.therapist_pay)}</td>
                                      <td style={detailTdStyle}>{money(d.practice_pay)}</td>
                                      <td style={{ ...detailTdStyle, fontWeight: 700 }}>
                                        {d.period_take_home != null ? money(d.period_take_home) : '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {hasMore && (
              <button onClick={() => fetchLog(false)} style={{ ...btnStyle, marginTop: '1rem' }} disabled={loading}>
                {loading ? 'Loading…' : 'Load Older Entries'}
              </button>
            )}
          </>
        )}
      </section>
    </div>
  );
}

/* ============================================================
   Styles (match AdminDailyReportPage look & feel)
============================================================ */
const containerStyle = {
  padding: '1.5rem',
  maxWidth: '1400px',
  margin: '0 auto',
  color: '#1f2937',
};
const pageHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem',
};
const cardStyle = {
  background: '#fff',
  border: '1px solid var(--border-color)',
  borderRadius: '10px',
  padding: '1.25rem',
  marginBottom: '1.25rem',
  boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
};
const tableWrapStyle = {
  overflowX: 'auto',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
};
const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.9rem',
};
const thStyle = {
  background: '#f9fafb',
  textAlign: 'left',
  padding: '10px 12px',
  borderBottom: '1px solid var(--border-color)',
  fontWeight: 600,
  color: 'var(--text-strong)',
  whiteSpace: 'nowrap',
};
const tdStyle = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--border-color)',
  verticalAlign: 'middle',
};
const detailTableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
  background: '#fff',
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
};
const detailThStyle = {
  background: '#eff6ff',
  textAlign: 'left',
  padding: '6px 10px',
  borderBottom: '1px solid var(--border-color)',
  fontWeight: 600,
  fontSize: '0.78rem',
  color: '#1e40af',
  whiteSpace: 'nowrap',
};
const detailTdStyle = {
  padding: '6px 10px',
  borderBottom: '1px solid var(--border-color)',
};
const inputStyle = {
  padding: '6px 8px',
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  fontSize: '0.9rem',
};
const btnStyle = {
  padding: '8px 14px',
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  background: '#fff',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: 500,
  color: '#1f2937',
};
const btnPrimaryStyle = {
  ...btnStyle,
  background: 'var(--accent-color)',
  color: '#fff',
  borderColor: '#2563eb',
};
const filtersRowStyle = {
  display: 'flex',
  gap: '0.75rem',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  marginBottom: '1rem',
};
const filterGroupStyle = { display: 'flex', flexDirection: 'column', gap: '3px' };
const filterLabelStyle = {
  fontSize: '0.8rem',
  color: '#4b5563',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};

export default AdminSubmissionLogPage;
